import OpenAI from 'openai';
import { fal } from '@fal-ai/client';
import { imageModels } from '../../components/ImageControls';

class ImageClient {
  constructor() {
    this.falClient = null;
    this.openaiClient = null;
    this.aiClient = null;
    this._availableModels = [];
    this._promptHistory = [];
  }

  setApiKeys(keys) {
    if (keys.fal) {
      fal.config({ credentials: keys.fal });
      this.falClient = fal;
      console.log('Fal.ai client initialized');
    }
    if (keys.openai) {
      this.openaiClient = new OpenAI({ apiKey: keys.openai, dangerouslyAllowBrowser: true });
      console.log('OpenAI client initialized');
    }
  }

  setAiClient(client) {
    this.aiClient = client;
    console.log('AI client set for ImageClient');
  }

  setAvailableModels(models) {
    this._availableModels = models;
    console.log('Available models set:', models);
  }

  getAuxiliaryModel() {
    const stored = localStorage.getItem('globalAiModel');
    if (stored && this._availableModels.some(m => m.value === stored)) {
      return stored;
    }
    return this._availableModels[0]?.value;
  }

  resetPromptHistory() {
    this._promptHistory = [];
    console.log('Prompt history reset for new article');
  }

  async enhancePrompt(basicPrompt, style = 'realistic', context = {}) {
    if (!this.aiClient) {
      console.warn('AI client not initialized, using original prompt');
      return basicPrompt;
    }

    try {
      const keyConcepts = [...(context.articleTitle?.split(' ') || []), ...(basicPrompt.split(' '))]
        .filter(word => word.length > 3 && !['the', 'and', 'for'].includes(word.toLowerCase()))
        .slice(0, 3).join(', ');

      const perspectives = [
        'close-up', 'wide-angle', 'birds-eye view', 'low-angle', 'side view', 
        'over-the-shoulder', 'rear view', 'frontal shot'
      ];
      const lightings = [
        'natural daylight', 'golden hour', 'soft diffused light', 'dramatic side lighting', 
        'twilight', 'harsh midday sun', 'overcast sky', 'moonlit night'
      ];
      const settings = [
        'natural landscape', 'urban environment', 'indoor setting', 'abstract background', 
        'rural scene', 'mountainous terrain', 'coastal area', 'forested area'
      ];
      const actions = [
        'in motion', 'at rest', 'interacting with environment', 'in a dynamic pose', 
        'viewed from a distance', 'close-up detail', 'in a group'
      ];

      const modelCapability = imageModels.find(m => m.value === context.model)?.capability || 'medium';
      const useRandom = modelCapability !== 'low';
      const perspective = useRandom ? perspectives[Math.floor(Math.random() * perspectives.length)] : 'wide-angle';
      const lighting = useRandom ? lightings[Math.floor(Math.random() * lightings.length)] : 'natural daylight';
      const setting = useRandom ? settings[Math.floor(Math.random() * settings.length)] : 'natural landscape';
      const action = actions[Math.floor(Math.random() * actions.length)];

      const negativeHistory = this._promptHistory.length > 0 
        ? `not similar to: ${this._promptHistory.join(', ')}` 
        : '';

      const baseInstructions = `
        IMPORTANT RULES:
        - NEVER include any text, writing, or labels
        - Each image must be visually distinct from others in this article
        - Focus on core concepts: ${keyConcepts}
        - Use ${perspective} perspective
        - Apply ${lighting} lighting
        - Set in ${setting}
        - Depict the subject ${action}
        - ${negativeHistory}
      `;

      // Dynamically incorporate the user-selected style into the prompt
      const systemPrompt = `
        Create a ${style.replace('_', ' ')} image for section "${basicPrompt}" in article "${context.articleTitle || ''}".
        ${baseInstructions}
        Guidelines:
        - Emphasize ${keyConcepts}
        - Ensure the style reflects ${style.replace('_', ' ')} characteristics (e.g., vibrant colors for neon art, imaginative elements for fantasy art)
        - Vary poses, compositions, and environmental details
        - Avoid abstract or off-topic elements
        ${modelCapability === 'low' ? 'Negative prompts: no blurry details, no low resolution, no repetitive compositions' : ''}
        Return only the prompt, no extra text.
      `;

      const auxModel = this.getAuxiliaryModel();

      if (!auxModel) {
        console.warn('No auxiliary model available, using original prompt');
        return basicPrompt;
      }

      const response = await this.aiClient.generateArticleContent({
        title: 'Image Prompt Enhancement',
        modelId: auxModel,
        customPrompt: systemPrompt
      });

      this._promptHistory.push(response.trim());
      console.log(`Enhanced prompt (style: ${style}):`, response);
      return response.trim();
    } catch (error) {
      console.error('Error enhancing prompt:', error);
      return basicPrompt;
    }
  }

  _mapDimensionToImageSize(dimension) {
    const dimensionMap = {
      'square_hd': { width: 1024, height: 1024 },
      'square': { width: 512, height: 512 },
      'portrait_4_3': { width: 768, height: 1024 },
      'portrait_16_9': { width: 576, height: 1024 },
      'landscape_4_3': { width: 1024, height: 768 },
      'landscape_16_9': { width: 1024, height: 576 }
    };

    const dims = dimensionMap[dimension] || { width: 512, height: 512 };
    return { width: dims.width, height: dims.height };
  }

  _mapDimensionToAspectRatio(dimension) {
    const aspectRatioMap = {
      'square_hd': '1:1',
      'square': '1:1',
      'portrait_4_3': '3:4',
      'portrait_16_9': '9:16',
      'landscape_4_3': '4:3',
      'landscape_16_9': '16:9'
    };

    return aspectRatioMap[dimension];
  }

  async generateImage({ 
    prompt, 
    model, 
    dimension = 'square', 
    style = 'realistic', 
    enhancePrompt = false, 
    context = {},
    enableSafetyFilter = true,
  }) {
    const maxAttempts = 2;
    let attempt = 0;
    let finalPrompt = prompt;
    let modelInfo = imageModels.find(m => m.value === model) || { capability: 'medium' };

    while (attempt < maxAttempts) {
      try {
        if (!this.falClient) {
          throw new Error('Fal.ai client not initialized');
        }

        if (enhancePrompt) {
          finalPrompt = await this.enhancePrompt(prompt, style, { ...context, model });
        }

        const negativePrompt = modelInfo.capability === 'low' 
          ? 'no blurry details, no low resolution, no abstract noise' 
          : '';

        // Add style to the prompt if not already enhanced
        if (!enhancePrompt && style && style !== 'realistic') {
          finalPrompt = `${style.replace('_', ' ')} style: ${finalPrompt}`;
        }

        const isIdeogram = model.includes('ideogram');

        const input = {
          prompt: finalPrompt,
          negative_prompt: negativePrompt,
          seed: Math.floor(Math.random() * 1000000),
          num_images: 1,
          enable_safety_checker: enableSafetyFilter
        };

        // Handle different models' parameter requirements
        if (isIdeogram) {
          // Ideogram uses aspect_ratio
          const aspectRatio = this._mapDimensionToAspectRatio(dimension);
          if (aspectRatio) {
            input.aspect_ratio = aspectRatio;
          } else {
            // Fallback to width/height if no aspect ratio mapping
            const imageSize = this._mapDimensionToImageSize(dimension);
            input.width = imageSize.width;
            input.height = imageSize.height;
          }
        } else {
          // Most other models use image_size
          input.image_size = dimension;
          input.num_inference_steps = model.includes('schnell') ? 4 : 18;
          input.style_name = style === 'realistic' ? '(No style)' : style;
          input.output_format = 'jpeg';
          input.guidance_scale = 5;
        }

        const result = await this.falClient.subscribe(model, {
          input,
          logs: true,
          onQueueUpdate: (update) => {
            if (update.status === "IN_PROGRESS") {
              update.logs?.map(log => log.message).forEach(msg => 
                console.log('🔄 Fal.ai progress:', msg)
              );
            }
            console.log('📊 Queue status:', update.status);
          }
        });

        if (!result?.data) {
          throw new Error('No data in response');
        }

        const imageUrl = result.data.images?.[0]?.url || result.data.image;
        
        if (!imageUrl) {
          console.error('Full result:', result);
          if (result.error?.message) {
            throw new Error(result.error.message);
          }
          throw new Error('No image URL in response - the content may have been filtered');
        }

        console.log(`Generated image with prompt: "${finalPrompt}" using ${model}`);
        return {
          status: 'success',
          image_url: imageUrl
        };

      } catch (error) {
        console.error(`Attempt ${attempt + 1} failed:`, error);
        attempt++;

        if (attempt === maxAttempts) {
          if (error.message?.includes('safety')) {
            throw new Error('Content was filtered due to safety restrictions. Please modify your prompt and try again.');
          }
          if (error.message?.includes('rate limit')) {
            throw new Error('Rate limit exceeded. Please wait a moment and try again.');
          }
          throw error;
        }

        finalPrompt = `${prompt} (simplified, clear depiction)`;
        if (modelInfo.capability === 'low') {
          model = imageModels.find(m => m.value === 'fal-ai/flux/dev')?.value;
          modelInfo = imageModels.find(m => m.value === model) || { capability: 'medium' };
          console.log('Switching to fallback model:', model);
        }
      }
    }
  }
}

export const imageClient = new ImageClient();
export default imageClient;